import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { en } from '@/i18n/locales/en';
import { fr } from '@/i18n/locales/fr';

export const SUPPORTED_LANGUAGES = ['en', 'fr'] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** localStorage key the detector reads/writes the chosen language to. */
export const LANGUAGE_STORAGE_KEY = 'nangbook-language';

/**
 * App-wide i18n. English is the default and fallback. The active language is
 * resolved from (in order) the user's stored choice in localStorage, then the
 * browser/OS locale; a signed-in user's saved `preferred_language` is applied
 * on top of this at runtime (see LocaleSync), and persists cross-device via the
 * server. `fr-FR` etc. collapse to `fr`.
 */
void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      fr: { translation: fr },
    },
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
  });

/**
 * The active language collapsed to a supported code — used for the
 * `Accept-Language` header so the backend (receipts, emails, validation) can
 * match the UI.
 */
export function currentApiLanguage(): AppLanguage {
  const language = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);

  return (SUPPORTED_LANGUAGES as readonly string[]).includes(language)
    ? (language as AppLanguage)
    : 'en';
}

export default i18n;
